import { Link } from "react-router";
import {
	House,
	CreditCard,
	Gift,
	Headset,
	User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { tenant } from "@/lib/tenant";

interface Tab {
	href?: string;
	action?: "support";
	icon: React.ElementType;
	label: string;
}

/* Destinations only — money actions (Deposit / Send) live next to the
   balance on Home, where they semantically belong. */
const tabs: Tab[] = [
	{ href: "/", icon: House, label: "Home" },
	{ href: "/referral", icon: Gift, label: "Refer & Earn" },
	{ href: "/cards", icon: CreditCard, label: "Cards" },
	// Support tab only when the tenant provides a Telegram link.
	...(tenant.telegramSupport
		? [
				{
					action: "support",
					href: tenant.telegramSupport,
					icon: Headset,
					label: "Support",
				} as Tab,
			]
		: []),
	{ href: "/profile", icon: User, label: "Profile" },
];

interface MobileBottomNavComponentProps {
	pathname: string;
}

export function MobileBottomNavComponent({
	pathname,
}: MobileBottomNavComponentProps) {
	return (
		<nav className="fixed bottom-3 left-3 right-3 z-50 md:hidden">
			<div className="bg-foreground rounded-2xl px-1 pb-[env(safe-area-inset-bottom)]">
				<div className="flex items-center justify-around h-[60px]">
					{tabs.map((tab) => {
						if (tab.action === "support") {
							return (
								<a
									key="support"
									href={tab.href!}
									target="_blank"
									rel="noopener noreferrer"
									className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
								>
									<tab.icon
										className="w-5 h-5 text-background/50 transition-colors duration-200"
										strokeWidth={1.8}
									/>
									<span className="text-[10px] text-background/50 font-medium">
										{tab.label}
									</span>
								</a>
							);
						}

						// Sub-pages keep their parent tab highlighted
					// (/order-card is part of the Cards flow).
					const isActive =
						tab.href === pathname ||
						(tab.href === "/cards" && pathname === "/order-card");

						return (
							<Link
								key={tab.href}
								to={tab.href!}
								className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
							>
								<tab.icon
									className={cn(
										"w-5 h-5 transition-colors duration-200",
										isActive ? "text-background" : "text-background/50"
									)}
									strokeWidth={1.8}
								/>
								<span
									className={cn(
										"text-[10px] font-medium transition-colors duration-200",
										isActive ? "text-background" : "text-background/50"
									)}
								>
									{tab.label}
								</span>
							</Link>
						);
					})}
				</div>
			</div>
		</nav>
	);
}
